import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { detect } from 'detect-browser';
import { AppText, Box, Column, styled, Button, AppIcon, useTheme } from '@streem/ui-react';
import { DeviceTroubleshootingModal } from '../device_troubleshooting/device_troubleshooting_modal';
import { Controls } from './controls';
import { Intro } from './intro';
import { useInvitationDetails } from '../../context/invitation_details';
import { useCompanySettings } from '../../context/company_settings';
import { VideoFramePreview } from '../in_call/video_frame/video_frame_preview';
import { useObservable, useVideoEnabled } from '../..';
import { useRoomSession } from '../../context/room_session';
import { recordScreenViewed } from '@streem/analytics';
import { JoinNowButton } from './join_now_button';
import { TestAudioModal } from '../test_audio_modal';
export function CallLobby({ startCall, initialized }) {
    const ANIMATION_DURATION = 350;
    const previewContainerRef = useRef(null);
    const cameraOffRef = useRef(null);
    const theme = useTheme();
    const hostRoomSession = useRoomSession();
    const { agentAvatar, agentName, agentBio, companyName } = useInvitationDetails();
    const { logoUrl } = useCompanySettings();
    const [localVideoController] = useObservable(hostRoomSession.media.localVideoController);
    const [audioState] = useObservable(hostRoomSession.media.localAudio.enabled);
    const [audioConnected] = useObservable(hostRoomSession.media.localAudio.connected);
    const [videoState] = useVideoEnabled(hostRoomSession.media.localVideoController);
    const [userMediaVideoError] = useObservable(hostRoomSession.media.getUserMediaVideoError);
    const [userMediaAudioError] = useObservable(hostRoomSession.media.getUserMediaAudioError);
    const [showAudioModal, setShowAudioModal] = useState(false);
    const [animating, setAnimating] = useState(false);
    const [deviceTroubleshootingModalConfig, setDeviceTroubleshootingModalConfig] = useState({ deviceType: 'Microphone', showDeviceTroubleshootingModal: false });
    const cameraPermissionsDenied = userMediaVideoError === 'NotAllowedError';
    const microphonePermissionsDenied = userMediaAudioError === 'NotAllowedError';
    useEffect(() => {
        recordScreenViewed('call: lobby');
    }, []);
    useEffect(() => {
        if (microphonePermissionsDenied) {
            setDeviceTroubleshootingModalConfig({
                deviceType: 'Microphone',
                showDeviceTroubleshootingModal: true,
            });
        }
        else if (cameraPermissionsDenied) {
            setDeviceTroubleshootingModalConfig({
                deviceType: 'Camera',
                showDeviceTroubleshootingModal: true,
            });
        }
    }, [microphonePermissionsDenied, cameraPermissionsDenied]);
    /*  NOTE: At the moment this only seems to work with Chrome/Android browsers.
        I'm honestly not sure why, since we're doing all the things that Safari wants us to do.
        But, even node packages like `fscreen` throw errors when trying to use the vendor-prefix polyfill
        Safari decides when to go fullscreen on it's own pretty well though, and the address bar is less obtrusive than Chrome.
    */
    const setFullScreenModeAndStartCall = () => {
        const docEl = document.documentElement;
        const isDev = process.env.NODE_ENV === 'development';
        const browser = detect();
        if (!isDev && browser?.os !== 'iOS') {
            try {
                docEl.requestFullscreen();
            }
            catch (error) {
                console.log("Couldn't go to fullscreen mode. Probably you're in webkit: ", error);
            }
        }
        // regardless, start the call.
        startCall();
    };
    const handleJoinNowClick = () => {
        if (previewContainerRef && previewContainerRef.current && !animating) {
            setAnimating(true);
            const { top, width, height } = previewContainerRef.current.getBoundingClientRect();
            const animationKeyframes = [
                {
                    top: '0px',
                    width: `${width}px`,
                    height: `${height}px`,
                },
                {
                    top: `-${top}px`,
                    width: `${window.innerWidth}px`,
                    height: `${window.innerHeight}px`,
                    borderRadius: '0px',
                },
            ];
            const animation = previewContainerRef.current.animate(animationKeyframes, {
                duration: ANIMATION_DURATION,
                fill: 'forwards',
            });
            animation.onfinish = () => {
                setAnimating(false);
                setFullScreenModeAndStartCall();
            };
        }
        else if (cameraOffRef && cameraOffRef.current && !animating) {
            setAnimating(true);
            const { top, width, height } = cameraOffRef.current.getBoundingClientRect();
            const animationKeyframes = [
                {
                    top: '0px',
                    width: `${width}px`,
                    height: `${height}px`,
                },
                {
                    top: `-${top}px`,
                    width: `${window.innerWidth}px`,
                    height: `${window.innerHeight}px`,
                    borderRadius: '0px',
                },
            ];
            const animation = cameraOffRef.current.animate(animationKeyframes, {
                duration: ANIMATION_DURATION,
                fill: 'forwards',
            });
            animation.onfinish = () => {
                setAnimating(false);
                setFullScreenModeAndStartCall();
            };
        }
        else {
            setFullScreenModeAndStartCall();
        }
    };
    return (_jsxs(_Fragment, { children: [deviceTroubleshootingModalConfig.showDeviceTroubleshootingModal && (_jsx(DeviceTroubleshootingModal, { permissionDenied: microphonePermissionsDenied || cameraPermissionsDenied, handleContinue: () => setDeviceTroubleshootingModalConfig({
                    deviceType: 'Microphone',
                    showDeviceTroubleshootingModal: false,
                }), inCall: false, device: deviceTroubleshootingModalConfig.deviceType })), _jsxs(CenteredColumn, { width: "100vw", top: 0, bottom: 0, position: "fixed", overflow: "overlay", backgroundColor: "inherit", children: [showAudioModal && (_jsx(TestAudioModal, { "data-testid": "test-audio-modal", onClose: () => setShowAudioModal(false) })), _jsx(Intro, { avatarSrc: agentAvatar, displayName: agentName, bio: agentBio, company: companyName, logo: logoUrl, size: "small" }), _jsx(Box, { mt: '1.5vh', mb: '2vh', width: "60%", children: _jsxs(_Fragment, { children: [_jsxs(VideoContainer, { "data-testid": "video-container", children: [videoState && localVideoController && (_jsx(VideoFramePreview, { videoController: localVideoController, ref: previewContainerRef, animating: animating })), !videoState && (_jsxs(CameraOffContainer, { ref: cameraOffRef, children: [_jsx(Box, { mb: 4, children: _jsx(AppIcon, { name: "Face", color: theme.colors.grey30, size: 'large' }) }), _jsx(AppText, { "data-testid": "camera-off-message", color: "grey30", children: "Your camera is off" })] }))] }), _jsx(Controls, { audioState: {
                                        enabled: initialized ? !!audioState : false,
                                        notAvailable: !audioConnected,
                                        toggleEnabled: () => {
                                            hostRoomSession.media.localAudio.setEnabled(!audioState);
                                        },
                                        showMicTroubleshootingMessage: () => {
                                            setDeviceTroubleshootingModalConfig({
                                                deviceType: 'Microphone',
                                                showDeviceTroubleshootingModal: true,
                                            });
                                        },
                                    }, videoState: {
                                        enabled: initialized ? !!videoState : false,
                                        notAvailable: cameraPermissionsDenied,
                                        toggleEnabled: () => {
                                            if (localVideoController) {
                                                localVideoController.setEnabled(!videoState);
                                            }
                                        },
                                        showCameraTroubleshootingMessage: () => {
                                            setDeviceTroubleshootingModalConfig({
                                                deviceType: 'Camera',
                                                showDeviceTroubleshootingModal: true,
                                            });
                                        },
                                    } })] }) }), _jsx(JoinNowButton, { onClick: handleJoinNowClick, disabled: !initialized || cameraPermissionsDenied }), cameraPermissionsDenied && (_jsx(AppText, { color: "red50", bold: true, style: { marginTop: '5px' }, children: "Camera access is required to join the call." })), _jsx(Box, { mt: "12px", children: _jsx(Button, { "data-testid": "test-audio-button", variant: "inline", disabled: !initialized, fontColor: initialized ? 'inherit' : 'grey20', onClick: () => setShowAudioModal(true), children: "Test Audio" }) })] })] }));
}
const VideoContainer = styled(Column)(({ theme }) => ({
    position: 'relative',
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.colors.grey40,
    backgroundColor: theme.colors.black,
    width: '100%',
    height: '40vh',
    borderRadius: '20px',
}));
const CameraOffContainer = styled(Column)(({ theme }) => ({
    position: 'absolute',
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.grey90,
    width: '100%',
    height: '100%',
    borderRadius: '20px',
}));
const CenteredColumn = styled(Column)({
    alignItems: 'center',
    justifyContent: 'center',
});
//# sourceMappingURL=call_lobby.js.map